import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, FileText, Upload } from "lucide-react";
import { cn } from "../../../utils";
import { Button } from "../../base/button/button";
import { DropZone } from "../drop-zone/drop-zone";
/** Thrown by a `parse` when no column header of the sheet matches the template. */ export class ExcelHeaderMismatchError extends Error {
}
/**
 * The one template-download card + dropzone block of every Excel bulk upload —
 * rendered inside the ExcelUploadDialog and inline by the setup wizard's
 * empty states. The caller's `parse` does the parsing and merges into its own
 * state; the surface only drives the file input and the success/failure toast.
 */ export function ExcelUploadSurface({ downloadTemplate, parse, onParsed, errorToast, showSuccessToast = true, showDivider = false, dropZoneClassName, accept = ".xlsx,.xls" }) {
    const { t } = useTranslation();
    const dropZoneId = useId();
    const [isUploading, setIsUploading] = useState(false);
    const handleFileUpload = async (files)=>{
        const file = files[0];
        if (!file) return;
        setIsUploading(true);
        try {
            const count = await parse(file);
            if (showSuccessToast) {
                toast.success(t("common.excelUpload.parsedRows", {
                    count
                }));
            }
            onParsed?.();
        } catch (error) {
            if (error instanceof ExcelHeaderMismatchError) {
                toast.error(t("common.excelUpload.headerMismatch"));
            } else {
                toast.error(errorToast ?? t("common.excelUpload.parseFailed"));
                console.error(error);
            }
        } finally{
            setIsUploading(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3.5 rounded-xl border border-line bg-white px-4 py-3.5",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "flex h-9.5 w-9.5 flex-none items-center justify-center rounded-[9px] bg-bliks-green-700 text-white",
                        children: /*#__PURE__*/ _jsx(FileText, {
                            className: "h-4.75 w-4.75"
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-bold text-foreground",
                                children: t("common.excelUpload.downloadTemplateTitle")
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-0.5 text-[12.5px] text-fg-secondary",
                                children: t("common.excelUpload.downloadTemplateDesc")
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        variant: "brand",
                        size: "small",
                        className: "h-9 gap-1.75 px-4 text-[13px] [&_svg]:size-3.75",
                        onClick: downloadTemplate,
                        children: [
                            /*#__PURE__*/ _jsx(Download, {}),
                            t("common.download")
                        ]
                    })
                ]
            }),
            showDivider && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3.5",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "h-px flex-1 bg-surface-strong"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-xs text-fg-subtle",
                        children: t("common.or")
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "h-px flex-1 bg-surface-strong"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DropZone, {
                id: dropZoneId,
                onChange: handleFileUpload,
                uploading: isUploading,
                accept: accept,
                className: cn("rounded-xl border-[1.5px] border-dashed border-line-strong bg-surface-subtle p-7.5", dropZoneClassName),
                icon: /*#__PURE__*/ _jsx("span", {
                    className: "mb-3.5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-white text-bliks-green-700",
                    children: /*#__PURE__*/ _jsx(Upload, {
                        className: "h-5 w-5"
                    })
                }),
                title: /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm text-foreground",
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            children: [
                                t("common.excelUpload.dragOrBrowse.prefix"),
                                " "
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-semibold text-bliks-green-700",
                            children: t("common.excelUpload.dragOrBrowse.browse")
                        })
                    ]
                }),
                description: /*#__PURE__*/ _jsx("div", {
                    className: "mt-1.25 text-xs text-fg-subtle",
                    children: t("common.excelUpload.maxFileSize")
                })
            })
        ]
    });
}

//# sourceMappingURL=excel-upload-surface.js.map